import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@mui/material';
import PropTypes from 'prop-types';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import API_BASE_URL from '../../../../Constants';
import { RenderFields } from '../Customer/RenderFields';
import CustomerAccordion from '../Customer/accordion/CustomerAccordion';
import { GeneralInfoFields, AddressInfoFields } from './taxDetailsConfig';
import { useAuth } from '../../../../contexts/AuthContext';



const ViewTaxDetailsModal = ({ open, handleClose, taxDetail, setTaxDetail, setNext, setPrev, allTaxDetails, updateAll }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  const [pendingRequest, setPendingRequest] = useState('');
  const [lockedModal, setLockedModal] = useState(false);
  const [lockOwner, setLockOwner] = useState('');
  const [eventSRC, setEventSRC] = useState(null);



  useEffect(() => {
    setIsEditMode(false);
  }, [open])

  useEffect(() => {
    const eventSource = new EventSource(`${API_BASE_URL}/sse/subscribe?username=${encodeURIComponent(user.username)}`);
    setEventSRC(eventSource);

    eventSource.onmessage = (event) => {
      alert(event.data);
    };

    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [user]);


  // componentUnmount useEffect
  useEffect(() => {
    return () => {
      if (eventSRC) {
        eventSRC.close();
      }
    };
  }, [eventSRC]);


  const clearUserLocks = async (user) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/record/${encodeURIComponent(user)}/clear-locks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        notifyRequesters(taxDetail.id);
      } else {
        throw new Error('Failed to clear locks');
      }
    } catch (error) {
      console.error('An error occurred:', error.message);
    }
  };



  // API CALL TO NOTIFY REQUESTERS
  const notifyRequesters = async (recordId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notify/requesters?recordId=${encodeURIComponent(recordId + "-taxdetail")}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',

      });

      if (!response.ok) {
        throw new Error('Failed to notify requesters');
      }
    } catch (error) {
      console.error('An error occurred:', error.message);
    }
  }

  const notifyUser = async (lockOwner, requester, recordId) => {
    try {
      await fetch(
        `${API_BASE_URL}/api/notify/owner?lockOwner=${encodeURIComponent(lockOwner)}&requester=${encodeURIComponent(requester)}&recordId=${encodeURIComponent(recordId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      alert('Notification sent successfully');
      setPendingRequest(null);
      setLockedModal(false);
    } catch (error) {
      alert('Failed to send notification:', error.message);
    }
  };

  const handleRequestAccess = () => {
    if (pendingRequest) {
      notifyUser(lockOwner, user.username, pendingRequest);

      const eventSource = new EventSource(`${API_BASE_URL}/sse/subscribe/record?recordId=${pendingRequest}&username=${user.username}`);
      eventSource.onmessage = ({ data }) => {
        alert('Record [ ' + data + ' ] lock has been cleared');
        setPendingRequest(null);
        setLockedModal(false);
        eventSource.close();
      };
    }
  };


  const handleEditModal = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/record/${taxDetail.id + '-taxdetail'}/lock?user=${encodeURIComponent(user.username)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        toggleEditMode();
      } else if (response.status === 401) {
        const owner = await response.text();
        setLockOwner(owner);
        setPendingRequest(taxDetail.id + '-taxdetail');
        setLockedModal(true);
      } else {
        throw new Error('Failed to lock record');
      }
    } catch (error) {
      alert('An error occurred: ' + error.message);
    }
  };



  // Toggle functions
  const toggleEditMode = () => {
    setIsEditMode((prevMode) => !prevMode);
  };



  const updateTaxDetails = (taxDetail, isDelete = false) => {
    let all = [...allTaxDetails]
    let index = all.findIndex((c) => String(c.id) === String(taxDetail.id));
    if (isDelete) {
      // delete index record
      all.splice(index, 1);
    } else {
      all[index] = taxDetail;
    }
    updateAll(all);
  }



  const handleSaveChanges = async () => {


    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/taxdetails/update/${taxDetail.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',

        },
        credentials: 'include',
        body: JSON.stringify(taxDetail),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();

      updateTaxDetails(data);
      clearUserLocks(user.username);
      setIsEditMode(false);
    } catch (error) {
      console.error('Fetch error: ', error);
    }
  };

  const handleDelete = async () => {
    try {

      await fetch(`${API_BASE_URL}/api/setup/taxdetails/delete/${taxDetail.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      updateTaxDetails(taxDetail, true);
      clearUserLocks(user.username);
      handleClose();

    } catch (error) {
      console.error('Fetch error: ', error);
    }

  }

  const handleModalClose = () => {
    if (isEditMode) {
      clearUserLocks(user.username);
    }
    handleClose();
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setTaxDetail((prev) => ({ ...prev, [name]: value }));
  };


  // Helper function to split fields array into two halves
  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };





  if (!taxDetail) return null;
  return (
    <>
      {open && (
        <>
          {/* Left Arrow Button */}
          <button
            onClick={setPrev}
            disabled={isEditMode}
            style={{
              position: 'fixed',
              left: `calc(50% - ${'500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${isEditMode ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            aria-label="Previous Customer"
          >
            <ArrowBackIcon />
          </button>
          {/* Right Arrow Button */}
          <button
            onClick={setNext}
            disabled={isEditMode}
            style={{
              position: 'fixed',
              right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${isEditMode
              ? 'opacity-50 cursor-not-allowed'
              : ''
              }`}
            aria-label="Next Customer"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}



      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
            minHeight: '90vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >



        {/* Header */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            {/* Left section: Back button and title */}
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleModalClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode
                  ? `Edit Tax Detail`
                  : `View Tax Detail`} - {taxDetail.description}
              </h2>
            </div>
            {/* Right section: Expand/Shrink Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          <div className="flex space-x-4 p-4">

            {isEditMode ? (
              <>
                <button
                  onClick={toggleEditMode}
                  className="px-4 py-2 bg-sss-light-gray text-sss-blue-sel font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={handleEditModal}
                className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>



        <DialogContent
          dividers
          sx={{
            padding: 0,
            maxHeight: '90vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >

          <div className="p-4 space-y-5 rounded-2xl">
            {/* General Info Accordion */}
            {(() => {
              const [generalInfoLeft, generalInfoRight] = splitFields(
                GeneralInfoFields
              );
              return (
                <CustomerAccordion label="General Info">
                  <div
                    className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                      } gap-4`}
                  >
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoLeft}
                        formData={taxDetail}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}

                      />
                    </div>
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoRight}
                        formData={taxDetail}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}

                      />
                    </div>

                  </div>


                </CustomerAccordion>


              );
            })()}

            {/* Address Info Fields */}
            {(() => {
              const [generalInfoLeft, generalInfoRight] = splitFields(
                AddressInfoFields
              );
              return (
                <CustomerAccordion label="Address Info">
                  <div
                    className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}
                  >
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoLeft}
                        formData={taxDetail}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}

                      />
                    </div>
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoRight}
                        formData={taxDetail}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}

                      />
                    </div>

                  </div>


                </CustomerAccordion>


              );
            })()}


          </div>


          {lockedModal && (
            <div className="fixed inset-0 overflow-y-auto z-50">
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                  <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span
                  className="hidden sm:inline-block sm:align-middle sm:h-screen"
                  aria-hidden="true"
                >
                  &#8203;
                </span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Record Locked
                        </h3>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            The record is currently being edited by{' '}
                            {lockOwner || 'someone'}. Do you want to
                            request access?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={handleRequestAccess}
                    >
                      Request Access
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => setLockedModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

ViewTaxDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  taxDetail: PropTypes.object,
  setTaxDetail: PropTypes.func.isRequired,
  setNext: PropTypes.func.isRequired,
  setPrev: PropTypes.func.isRequired,
  allTaxDetails: PropTypes.array.isRequired,
  updateAll: PropTypes.func.isRequired,
};

export default ViewTaxDetailsModal;
